import { Link, useNavigate } from "react-router-dom"
import styled from "styled-components"
import { Button } from "~/components"
import Toast from "~/modules/toasts/Toast"
import { useTranslate } from "~/ur-react-hooks"
import { useToast } from "~/utils"
import { format } from "~/utils/date-fns/format"
import { useErrorHandling } from "~/utils/error"
import { useOrganizationMembership } from "~/utils/hooks/useOrganizationMembership"
import { trpc } from "~/utils/trpc"

const Layout = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 6rem;
`

const Card = styled.div`
  background: white;
  box-shadow: ${(props) => props.theme.shadow.elevation3};
  padding: 2rem;
  width: 456px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 356px;
`

const Title = styled.h4`
  font-size: 18px;
  color: ${(props) => props.theme.colors.gray0};
`

const LastAccessInfo = styled.p``

const Actions = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`

const ConfirmCancel = styled.button`
  margin-top: 0.5rem;
  ${(props) => props.theme.button.reset}
  color: ${(props) => props.theme.colors.gray60};

  &:hover {
    text-decoration: underline;
  }
`

export type SubscriptionSchoolCancelProps = {}

const SubscriptionSchoolCancel: React.FC<
  SubscriptionSchoolCancelProps
> = () => {
  const organizationMembership = useOrganizationMembership()!
  const translations = useTranslate({
    title: "subscription-school.cancel.title",
    toastText: "subscription-school.cancel.toast-text",
    lastAccessInfo: [
      "subscription-school.cancel.last-access-info",
      { date: "20022-01-01" },
    ],
    keepSubscription: "subscription-school.cancel.keep-subscription",
    confirmCancel: "cancel-subscription.confirm-cancel",
  })

  const navigate = useNavigate()

  const { data: schoolSubscription } =
    trpc.subscriptions.getOrganizationSubscription.useQuery(
      organizationMembership.organizationId,
    )

  const lastAccessDateFormatted = format(
    schoolSubscription?.currentPeriodEnd ?? new Date(),
    "dd.MM.yyyy",
  )

  const toast = useToast()
  const { getToastInfo } = useErrorHandling()

  const { mutate: cancelSubscription } =
    trpc.subscriptions.cancelOrganizationSubscription.useMutation({
      onSuccess() {
        navigate("/subscription/school-cancel-success")
      },
      onError(error) {
        const { type, message } = getToastInfo(
          error.message,
          error.data?.code,
          "error",
        )

        toast(type, message)
      },
    })

  return (
    <Layout>
      <Card>
        <Content>
          <Title>{translations.title}</Title>
          <Toast type="warning" message={translations.toastText} />
          <LastAccessInfo>
            {translations.lastAccessInfo({
              date: lastAccessDateFormatted,
            })}
          </LastAccessInfo>
          <Actions>
            <Link to="/Home">
              <Button width="255px">{translations.keepSubscription}</Button>
            </Link>
            <ConfirmCancel
              onClick={() =>
                cancelSubscription(organizationMembership.organizationId)
              }
            >
              {translations.confirmCancel}
            </ConfirmCancel>
          </Actions>
        </Content>
      </Card>
    </Layout>
  )
}

export default SubscriptionSchoolCancel
